import { Box, Card, CardContent, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import { Helmet } from 'react-helmet-async';
import { superAdminApi } from '../../api/superAdminApi';
import { PageHeader } from '../../components/PageHeader';
import { COMPETITION_STATUS_LABELS } from '../../types/domain';
import { formatDateCz } from '../../utils/dateFormat';

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  hint?: string;
}

const MetricCard = ({ icon, label, value, hint }: MetricCardProps) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{ color: 'primary.main' }}>{icon}</Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {value}
          </Typography>
          {hint && (
            <Typography variant="caption" color="text.secondary">
              {hint}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export const SuperAdminDashboardPage = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['superadmin', 'dashboard'],
    queryFn: superAdminApi.dashboard,
  });

  return (
    <>
      <Helmet>
        <title>Dashboard | SUPER ADMIN</title>
      </Helmet>
      <PageHeader title="Dashboard" subtitle="Globální přehled celého systému" />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Typography color="error">Chyba při načítání dashboard dat.</Typography>
      )}

      {data && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                icon={<GroupsIcon sx={{ fontSize: 40 }} />}
                label="Jednotek"
                value={data.totalUnits}
                hint={`${data.activeUnits} aktivních`}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                icon={<PeopleIcon sx={{ fontSize: 40 }} />}
                label="Uživatelů"
                value={data.totalUsers}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                icon={<EmojiEventsIcon sx={{ fontSize: 40 }} />}
                label="Soutěží celkem"
                value={data.totalCompetitions}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                icon={<LiveTvIcon sx={{ fontSize: 40, color: 'error.main !important' }} />}
                label="Probíhá teď"
                value={data.liveCompetitions}
              />
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Probíhající soutěže
              </Typography>
              {data.liveList.length === 0 && (
                <Typography color="text.secondary">Žádná soutěž právě neběží.</Typography>
              )}
              {data.liveList.map((c) => (
                <Stack key={c.id} direction="row" justifyContent="space-between" sx={{ py: 1 }}>
                  <Box>
                    <Typography fontWeight={600}>{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.unitName} • {formatDateCz(c.competitionDate)} • {COMPETITION_STATUS_LABELS[c.status]}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    /s/{c.shortCode}
                  </Typography>
                </Stack>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
};
